import React from "react";
import { Box, Chip, SvgIconProps, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { TimelineOutcomeEnum } from "@/domain/troubleshooting.enum";

/**
 * COYN-1507: outcome colour + icon. Each outcome has a distinct icon shape
 * (check / minus / cross / "i") so the timeline never relies on colour alone.
 */
export const OUTCOME_META: Record<
  TimelineOutcomeEnum,
  { color: string; Icon: React.ComponentType<SvgIconProps> }
> = {
  [TimelineOutcomeEnum.OK]: { color: "#2E7D32", Icon: CheckCircleIcon },
  [TimelineOutcomeEnum.SKIPPED]: { color: "#B26A00", Icon: RemoveCircleIcon },
  [TimelineOutcomeEnum.FAILED]: { color: "#C62828", Icon: CancelIcon },
  [TimelineOutcomeEnum.INFO]: { color: "#546E7A", Icon: InfoIcon },
};

export const OUTCOME_ORDER: TimelineOutcomeEnum[] = [
  TimelineOutcomeEnum.OK,
  TimelineOutcomeEnum.SKIPPED,
  TimelineOutcomeEnum.FAILED,
  TimelineOutcomeEnum.INFO,
];

const metaFor = (outcome: TimelineOutcomeEnum) =>
  OUTCOME_META[outcome] || OUTCOME_META[TimelineOutcomeEnum.INFO];

export const useOutcomeLabel = () => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string) => string;
  return (outcome: TimelineOutcomeEnum) => tt(`outcome.${outcome}`);
};

export const OutcomeIcon: React.FC<{
  outcome: TimelineOutcomeEnum;
  fontSize?: SvgIconProps["fontSize"];
  /** Overrides the outcome colour (e.g. white inside a filled marker). */
  color?: string;
  sx?: SvgIconProps["sx"];
}> = ({ outcome, fontSize = "small", color, sx }) => {
  const meta = metaFor(outcome);
  return (
    <meta.Icon
      aria-hidden
      fontSize={fontSize}
      sx={{ color: color ?? meta.color, ...(sx as object) }}
      data-outcome={outcome}
    />
  );
};

export const outcomeColor = (outcome: TimelineOutcomeEnum): string =>
  metaFor(outcome).color;

export const OutcomeChip: React.FC<{ outcome: TimelineOutcomeEnum }> = ({
  outcome,
}) => {
  const label = useOutcomeLabel();
  const { color } = metaFor(outcome);
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<OutcomeIcon outcome={outcome} />}
      label={label(outcome)}
      sx={{ borderColor: color, color, fontWeight: 600 }}
    />
  );
};

export const TimelineLegend: React.FC = () => {
  const { t } = useTranslation("troubleshooting");
  const label = useOutcomeLabel();
  return (
    <Box
      component="ul"
      aria-label={t("legend")}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        listStyle: "none",
        p: 0,
        m: 0,
      }}
    >
      {OUTCOME_ORDER.map((outcome) => (
        <Box
          component="li"
          key={outcome}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <OutcomeIcon outcome={outcome} />
          <Typography variant="body2">{label(outcome)}</Typography>
        </Box>
      ))}
    </Box>
  );
};
